import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  SearchRounded,
} from '@mui/icons-material';
import {
  InputAdornment,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as styles from '../companies.styles';
import type Company from '../models/company';
import CompanyCard from '../cards/company-card.component';
import ActivityIndicator
  from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton
  from '../../../core/components/buttons/primary-button.component';
import { Center }
  from '../../../core/components/center/center.component';
import Column
  from '../../../core/components/column/column.component';
import {
  CrossAxisAlignment,
  MainAxisAlignment,
} from '../../../core/components/flex/flex.component';
import Row
  from '../../../core/components/row/row.component';
import { IsMobileContext }
  from '../../../core/context/is-mobile';
import RouteConstants from '../../../core/routes.constants';
import StyleConstants from '../../../core/styles.constants';
import CommonStyles from '../../../core/common.styles';
import Grid
  from '../../../core/components/grid/grid.component';
import * as filterStyles from './company-group.styles';

const ITEMS_PER_PAGE = 8;
const ITEMS_PER_PAGE_MOBILE = 6;

interface Props {
  readonly allCompanies: Company[] | undefined;
}

const CompanyOverviewGroup = ({
  allCompanies,
}: Props): React.JSX.Element => {
  const navigate = useNavigate();
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const isLoading = allCompanies == null;
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  const companiesPerPage = isMobile
    ? ITEMS_PER_PAGE_MOBILE
    : ITEMS_PER_PAGE;

  const sortedCompanies = useMemo(
    () => [...(allCompanies ?? [])].sort(
      (a, b) => (
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      ),
    ),
    [allCompanies],
  );

  const filteredCompanies = useMemo(() => {
    let result = sortedCompanies;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) => c.name.toLowerCase().includes(q),
      );
    }
    return result;
  }, [sortedCompanies, searchQuery]);

  const amountOfPages = Math.max(
    1,
    Math.ceil(filteredCompanies.length / companiesPerPage),
  );

  const paginatedCompanies = filteredCompanies.slice(
    page * companiesPerPage,
    (page + 1) * companiesPerPage,
  );

  const navigateToCreate = useCallback(() => {
    navigate(
      `/${RouteConstants.PATH_COMPANIES}${RouteConstants.PATH_CREATE}`,
    );
  }, [navigate]);

  const navigateToEdit = useCallback(
    (id: string) => {
      navigate(`/${RouteConstants.PATH_COMPANIES}/${id}`);
    },
    [navigate],
  );

  const showNextPage = (): void => {
    setPage((prev) => Math.min(prev + 1, amountOfPages - 1));
  };

  const showPreviousPage = (): void => {
    setPage((prev) => Math.max(prev - 1, 0));
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <div className={style(CommonStyles.sectionCard(isMobile))}>
      {isMobile ? (
        <Column
          className={style(styles.headerMargin)}
          crossAxisAlignment={CrossAxisAlignment.Stretch}
        >
          <span
            className={style(styles.columnHeader(isMobile))}
          >
            {t('companies').toUpperCase()}
          </span>
          <div className={style(styles.button(isMobile))}>
            <PrimaryButton
              fullWidth
              onClick={navigateToCreate}
              disabled={isLoading}
            >
              {t('create-new-company')}
            </PrimaryButton>
          </div>
        </Column>
      ) : (
        <Row
          className={style(styles.headerMargin)}
          mainAxisAlignment={MainAxisAlignment.SpaceBetween}
        >
          <span
            className={style(styles.columnHeader(isMobile))}
          >
            {t('companies').toUpperCase()}
          </span>
          <div className={style(styles.button(isMobile))}>
            <PrimaryButton
              onClick={navigateToCreate}
              disabled={isLoading}
            >
              {t('create-new-company')}
            </PrimaryButton>
          </div>
        </Row>
      )}

      {!isLoading && (allCompanies?.length ?? 0) > 0 && (
        <div
          className={style(
            filterStyles.filterContainer(isMobile),
          )}
        >
          <TextField
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={String(t('search-by-name'))}
            sx={filterStyles.searchFieldSx(isMobile)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    sx={filterStyles.searchIconSx}
                  />
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}

      {((): React.JSX.Element => {
        if (isLoading) {
          return (
            <Center>
              <div className={style(styles.rowItem())}>
                <ActivityIndicator
                  size={20}
                  color={StyleConstants.GREY_COLOR}
                />
              </div>
            </Center>
          );
        }

        if (
          allCompanies == null
          || allCompanies?.length === 0
        ) {
          return (
            <span
              className={style(styles.emptyListMessage)}
            >
              {t('no-companies-available')}
            </span>
          );
        }

        if (filteredCompanies.length === 0) {
          return (
            <span
              className={style(styles.emptyListMessage)}
            >
              {t('no-results-found')}
            </span>
          );
        }

        return (
          <Column className={style(styles.maxHeight)}>
            <div>
              <Grid
                rowClass={style(styles.gridRow)}
                numberOfColumns={1}
                numberOfItems={paginatedCompanies.length}
                renderItem={(
                  index,
                ): React.JSX.Element => {
                  const company = paginatedCompanies[index];
                  return (
                    company && (
                      <div
                        className={
                          isMobile
                            ? style(styles.rowItem(true))
                            : undefined
                        }
                        key={company.id}
                      >
                        <CompanyCard
                          key={company.id}
                          id={company.id ?? ''}
                          name={company.name}
                          officeCount={company.officeCount ?? 0}
                          onClick={(): void => {
                            navigateToEdit(
                              company.id ?? '',
                            );
                          }}
                        />
                      </div>
                    )
                  );
                }}
              />
            </div>
            {amountOfPages > 1 && (
              <Row
                mainAxisAlignment={
                  MainAxisAlignment.SpaceBetween
                }
                crossAxisAlignment={
                  CrossAxisAlignment.Center
                }
              >
                <ChevronLeftRounded
                  className={style(styles.tooltipIcon)}
                  onClick={showPreviousPage}
                  style={{
                    opacity: page === 0 ? 0.3 : 1,
                  }}
                />
                <div>
                  <span
                    className={style(
                      styles.columnHeader(isMobile),
                    )}
                  >
                    {t('page')} {page + 1}/{amountOfPages}
                  </span>
                </div>
                <ChevronRightRounded
                  className={style(styles.tooltipIcon)}
                  onClick={showNextPage}
                  style={{
                    opacity: page + 1 >= amountOfPages
                      ? 0.3
                      : 1,
                  }}
                />
              </Row>
            )}
          </Column>
        );
      })()}
    </div>
  );
};

export default CompanyOverviewGroup;
